import { ActionTree } from 'vuex'
import { ScriptPageState } from './state'
import { RootState } from '@/store/state'
import { SCRIPTS__FETCH_ONE } from '@/store/actions'
import {
  ScriptPageMutationTypes,
  ScriptPagePreviewMutationPayload,
  ScriptPageSetCardMutationPayload,
} from './mutations'
import scriptsApiService from '@/services/scripts'
import { CardOption } from '@/models/scripts'
import { CardVariant, CardOptionType } from '@/constants/scripts'
import router, { RouteNames } from '@/router'

export enum ScriptPageActionTypes {
  Preview = '[Script Page Action] Preview',
  GetCard = '[Script Page Action] Get Card',
  Move = '[Script Page Action] Move',
  Back = '[Script Page Action] Back',
}

export type ScriptPagePreviewPayload = {
  scriptId: string
  cardNum: number
  instanceId?: string
}

export type ScriptPageGetCardPayload = {
  scriptId: string
  cardNum: number
  instanceId?: string
}

export type ScriptPageMovePayload = {
  option: CardOption
  instanceId?: string
}

export type ScriptPageBackPayload = {
  cardNum: number
  instanceId?: string
}

export const actions: ActionTree<ScriptPageState, RootState> = {
  async [ScriptPageActionTypes.Preview](
    context,
    payload: ScriptPagePreviewPayload,
  ) {
    const { cardNum, scriptId, instanceId } = payload

    if (context.getters.getScriptById(scriptId) === undefined) {
      await context.dispatch(SCRIPTS__FETCH_ONE, { id: scriptId })
    }

    const getCardPayload: ScriptPageGetCardPayload = {
      scriptId,
      cardNum,
      instanceId,
    }

    const response = await context.dispatch(
      ScriptPageActionTypes.GetCard,
      getCardPayload,
    )

    const previewPayload: ScriptPagePreviewMutationPayload = {
      scriptId,
    }

    context.commit(ScriptPageMutationTypes.Preview, previewPayload)

    return response
  },

  async [ScriptPageActionTypes.GetCard](
    context,
    payload: ScriptPageGetCardPayload,
  ) {
    const { scriptId, cardNum, instanceId } = payload

    const response = await scriptsApiService.getScriptCardContent(
      scriptId,
      `n${cardNum}`,
      instanceId,
    )

    const { data: card } = response

    const setCardPayload: ScriptPageSetCardMutationPayload = { card }

    context.commit(ScriptPageMutationTypes.SetCard, setCardPayload)

    return response
  },

  async [ScriptPageActionTypes.Move](context, payload: ScriptPageMovePayload) {
    const { option, instanceId } = payload

    if (option.type === CardOptionType.Final) {
      const scriptId = context.getters.getScriptPageId
      const scriptIsTemplate = context.getters.getScriptById(scriptId)?.sample
      return router.push({
        name: scriptIsTemplate
          ? RouteNames.ScriptTemplates
          : RouteNames.ScriptsPage,
      })
    }

    if (option.type === CardOptionType.Card) {
      const scriptId = context.getters.getScriptPageId
      const getCardPayload: ScriptPageGetCardPayload = {
        scriptId,
        instanceId,
        cardNum: parseInt(option.link.toString(), 10),
      }

      context.commit(ScriptPageMutationTypes.SetVariant, CardVariant.A)
      return context.dispatch(ScriptPageActionTypes.GetCard, getCardPayload)
    }

    if (option.type === CardOptionType.Script) {
      return router.push({
        name: RouteNames.ScriptPage,
        params: { scriptId: option.link },
      })
    }
  },

  async [ScriptPageActionTypes.Back](context, payload: ScriptPageBackPayload) {
    const { cardNum, instanceId } = payload

    const scriptId = context.getters.getScriptPageId
    const getCardPayload: ScriptPageGetCardPayload = {
      scriptId,
      instanceId,
      cardNum,
    }

    return context.dispatch(ScriptPageActionTypes.GetCard, getCardPayload)
  },
}
